import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { environment } from '@env/environment';
import { IBillableReportParameters, IPastLogins, ISpcMethodAnalyte } from 'app/models/report/reportParameters';
import { IViewOsa, IViewPtModel } from 'app/models/report/reports';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

const httpOptionText = {
  headers: new HttpHeaders({
    'Content-Type': 'text/plain'
  })
};

@Injectable({
  providedIn: 'root',
})

export class ReportService {
  private reportApi: string = environment.privateApi + 'Reports/';
  
  public reportServerPath: string = '';
  public reportApplicationInvoice: string = 'APPLICATIONINVOICE';
  public reportActiveInactiveUsers: string = 'ACTIVEINACTIVEUSERS';
  public reportAssessReport: string = 'ASSESSREPORT';
  public reportBadEpaId: string = 'BADEPAID';
  public reportBillablesReport: string = 'BILLABLESREPORT';
  public reportCertificateReportData: string = 'CERTIFICATEREPORTDATA';
  public reportCompleteApplications: string = 'COMPLETEAPPLICATIONS';
  public reportDownloadExpDate: string = 'DOWNLOADEXPDATE';
  public reportEpaRegionX: string = 'EPAREGIONX';
  public reportFotRpt: string = 'FOTRPT';
  public reportInstrumentTechnologyWithFot: string = 'INSTRUMENTTECHNOLOGYWITHFOT';
  public reportLabsWithPublicTesting: string = 'LABSWITHPUBLICTESTING';
  public reportMailingAddress: string = 'MAILINGADDRESS';
  public reportMailingLabel: string = 'MAILINGLABEL';
  public reportOrelapNumber: string = 'ORELAPNUMBER';
  public reportOutOfStateBilling: string = 'OUTOFSTATEBILLING';
  public reportPrimaryInOutFee: string = 'PRIMARYINOUTFEE';
  public reportPrintAttachedReport: string = 'PRINTATTACHEDREPORT';
  public reportPrintSearchFoas: string = 'PRINTSEARCHFOAS';
  public reportRenewalAddressEnvelope: string = 'RENEWALADDRESSENVELOPE';
  public reportRenewalWarningLetter: string = 'RENEWALWARNINGLETTER';
  public reportCannabis: string = 'REPORTCANNABIS';
  public reportInvoiceData: string = 'REPORTINVOICEDATA';
  public reportOsa: string = 'REPORTOSA';
  public reportReportProjectedFees: string = 'REPORTPROJECTEDFEES';
  public reportSampleTypeRpt: string = 'SAMPLETYPERPT';
  public reportSecondaryOutFee: string = 'SECONDARYOUTFEE';
  public reportSingleMailingLabel: string = 'SINGLEMAILINGLABEL';
  public reportSpcMethodAnalyte2: string = 'SPCMETHODANALYTE2';
  public reportUploadRpt: string = 'UPLOADRPT';
  public reportUserLogin: string = 'USERLOGIN';
  public report: string = '';

  loadReportServerPath() {
    this.getPowerBiReportServerPath().subscribe(
      (result) => {
        this.reportServerPath = result;
      },
      error => {
        console.log('error: ', error);
        alert(error);
      },
      () => { }
    );
  }

  constructor(private http: HttpClient) { }

  // **************************** Report Server *********************************

  getPowerBiReportServerPath(): Observable<string> {
    return this.http.get(this.reportApi + 'getPowerBiReportServerPath', { responseType: 'text' as 'text' });
  }

  getReportData(reportName: string): Observable<string> {
    return this.http.get(this.reportApi + 'getReportData/' + reportName, { responseType: 'text' as 'text' });
  }
  
  // ****************************  Application Reports *********************************

  printInvoice(invoiceId: number): Observable<void> {
    return this.http.get<void>(this.reportApi + 'printInvoice/' + invoiceId);
  }

  getTniDemogReport(search: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'getTniDemogReport/' + search, { responseType: 'blob' as 'json' });
  }

  getTniFoaReport(search: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'getTniFoaReport/' + search, { responseType: 'blob' as 'json' });
  }

  // getBillablesParameters(): Observable<IBillableReportParameters> {
  //   return this.http.get<IBillableReportParameters>(this.reportApi + 'getBillablesParameters', httpOptions);
  // }

  //****************************  Demographics *********************************

  demogLicenseExpired(): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'demogLicenseExpired', { responseType: 'blob' as 'json' });
  }

  accreditedTni(): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'accreditedTni', { responseType: 'blob' as 'json' });
  }

  spcMethodAnalyte(search: ISpcMethodAnalyte): Observable<Blob> {
    return this.http.post<Blob>(this.reportApi + 'spcMethodAnalyte', search, { responseType: 'blob' as 'json' });
  }

  publicTesting(oregonOnly: string, publicTesting: string, matrix: string, orderBy: string, fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'publicTesting/' + oregonOnly + '/' + publicTesting
      + '/' + matrix + '/' + orderBy + '/' + fileType, { responseType: 'blob' as 'json' });
  }

  mailingAddress(oregonOnly: string, orderBy: string, fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'mailingAddress/' + oregonOnly + '/' + orderBy
      + '/' + fileType, { responseType: 'blob' as 'json' });
  }

  orelapNumber(oregonOnly: string, primarySecondary: string, inOrOut: string, currentOrClose: string,
    orderBy: string, fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'orelapNumber/' + oregonOnly + '/' + primarySecondary + '/'
      + inOrOut + '/' + currentOrClose + '/'
      + orderBy + '/' + fileType, { responseType: 'blob' as 'json' });
  }

  //****************************  Cash  *********************************

  feeReportPrimary(inOrOut: string, fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'feeReportPrimary/' + inOrOut + '/'
      + fileType, { responseType: 'blob' as 'json' });
  }

  feeReportSecondary(fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'feeReportSecondary/' + fileType, { responseType: 'blob' as 'json' });
  }

  outOfStateBilling(paid: string, fromDate: string, toDate: string, fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'outOfStateBilling/' + paid + '/'
      + fromDate + '/' + toDate + '/' + fileType, { responseType: 'blob' as 'json' });
  }

  // ********************************** Specialty Reports **********************************

  sampleTypeRpt(sampleType: string, accredited: string, oregonOnly: string, orderBy: string,
    fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'sampleTypeRpt/' + sampleType + '/'
      + accredited + '/' + oregonOnly + '/' + orderBy + '/' + fileType, { responseType: 'blob' as 'json' });
  }

  instrumentTech(fot: string, oregonOnly: string, fileType: string, list: string[]): Observable<Blob> {
    return this.http.post<Blob>(this.reportApi + 'instrumentTech/' + fot + '/'
      + oregonOnly + '/' + fileType, list, { responseType: 'blob' as 'json' });
  }

  getInstrumentTech(): Observable<string[]> {
    return this.http.get<string[]>(this.reportApi + 'getInstrumentTech', httpOptions);
  }

  getFot(): Observable<string[]> {
    return this.http.get<string[]>(this.reportApi + 'getFot', httpOptions);
  }

  applicationStatus(fromDate: string, toDate: string, status: string, orderBy: string,
    fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'applicationStatus/' + orderBy + '/' + fromDate + '/'
      + toDate + '/' + status + '/' + fileType, { responseType: 'blob' as 'json' });
  }

  userStatus(status: string, orderBy: string, fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'userStatus/' + status + '/'
      + orderBy + '/' + fileType, { responseType: 'blob' as 'json' });
  }

  fots(fotType: string, fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'fots/' + fotType + '/'
      + fileType, { responseType: 'blob' as 'json' });
  }

  osa(orderBy: string, filterBy: string, fromDate: string, toDate: string): Observable<IViewOsa> {
    return this.http.get<IViewOsa>(this.reportApi + 'osa/' + orderBy + '/'
      + filterBy + '/' + fromDate + '/' + toDate, httpOptions);
  }

  // osa(orderBy: string, filterBy: string, fromDate: string, toDate: string,
  //   fileType: string): Observable<Blob> {
  //   return this.http.get<Blob>(this.reportApi + 'osa/' + orderBy + '/'
  //     + filterBy + '/' + fromDate + '/' + toDate + '/' + fileType, { responseType: 'blob' as 'json' });
  // }

  epaRegion(fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'epaRegion/' + fileType, { responseType: 'blob' as 'json' });
  }

  assessmentReport(fromDate: string, toDate: string, labName: string, orderBy: string,
    inspectionStatus: string, fileType: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'assessmentReport/' + fromDate + '/'
      + toDate + '/' + orderBy + '/' + inspectionStatus + '/' + fileType
      + '/' + labName, { responseType: 'blob' as 'json' });
  }

  // cannabisReport(oregonOnly: string, fileType: string): Observable<Blob> {
  //   return this.http.get<Blob>(this.reportApi + 'cannabisReport/' + oregonOnly + '/'
  //     + fileType, { responseType: 'blob' as 'json' });
  // }

  cannabisReport(oregonOnly: string): Observable<number> {
    return this.http.get<number>(this.reportApi + 'cannabisReport/' + oregonOnly 
      , httpOptionText);
  }

  // ********************************* Misc ******************************************

  projectedFees(fromDate: string, toDate: string, applicationType: string, onsite: string,
    inState: string, fileType: string): Observable<boolean> {
    return this.http.get<boolean>(this.reportApi + 'projectedFees/' + fromDate + '/'
      + toDate + '/' + applicationType + '/' + onsite + '/' + inState + '/' + fileType,
      httpOptions);
  }

  userAccess(fromDate: string, toDate: string): Observable<Blob> {
    return this.http.get<Blob>(this.reportApi + 'userAccess/' + fromDate + '/'
      + toDate, { responseType: 'blob' as 'json' });
  }

  getAccessData(fromDate: string, toDate: string): Observable<IPastLogins[]> {
    console.log('Getting access data for dates: ', fromDate, toDate);
    return this.http.get<IPastLogins[]>(this.reportApi + 'getAccessData/' + fromDate + '/'
      + toDate, httpOptions);
  }

  invoices(fromDate: string, toDate: string, applicationType: string, inState: string,
    payments: string, invoices: string): Observable<number> {
    return this.http.get<number>(this.reportApi + 'invoices/' + fromDate + '/'
      + toDate + '/' + applicationType + '/' + inState + '/' + payments
      + '/' + invoices, httpOptionText);
  }

  // *********************************  PT Reports **********************************

  printLabPt(matrixFilter: string, failedAnalytes: string, badAnalytes: string, //orderbyTxt: string,
     appId: number, orelapId: string, fromDate: string, 
    toDate: string): Observable<IViewPtModel> {
    return this.http.get<IViewPtModel>(this.reportApi + 'printLabPt/' 
      + matrixFilter + '/' + failedAnalytes + '/' + badAnalytes //+ '/' + orderbyTxt 
      + '/' + appId + '/' + orelapId + '/' + fromDate + '/' + toDate, httpOptions);
  }

  printPassedPt(appId: string, orelapId: string, fromDate: string, toDate: string): Observable<IViewPtModel> {
    return this.http.get<IViewPtModel>(this.reportApi + 'printPassedPt/' + appId + '/'
      + orelapId + '/' + fromDate + '/' + toDate, httpOptions);
  }

  printFailedPt(appId: string, orelapId: string, fromDate: string, toDate: string): Observable<IViewPtModel> {
    return this.http.get<IViewPtModel>(this.reportApi + 'printFailedPt/' + appId + '/'
    + orelapId + '/' + fromDate + '/' + toDate, httpOptions);
  }

}
